// Shared-UI message dictionary (English plus French, German, Italian).
//
// User-facing copy renders through `t(key, vars)` against one table per
// locale. English (`en`) is the canonical source: every other locale mirrors
// it key for key with identical `{placeholders}`. Lookups fall back to
// English per key, so a missing translation never renders `undefined`.
// There is deliberately one lookup path only: a new locale adds a sibling
// table under `locales/` plus a `SUPPORTED_LOCALES` entry, never a second
// dictionary shape.
//
// Locale selection: hosts call `setLocale()` with an explicit picker choice,
// or `pickLocale()` with an `Accept-Language` header value or a
// `navigator.languages` list. Unknown tags fail closed to English.
// The website documents the picker in `docs/user/website.md`; help bodies
// stay English and regenerate via `scripts/build-help.mjs`.
//
// Rules (see `packages/shared-ui/AGENTS.md`):
// - User copy goes through `t()`; stable codes, diagnostics, technical logs,
//   URLs, transport codes, and protocol strings stay literal English.
// - Brand and product names ("Dezoomify", "Chrome Web Store", "GitHub
//   Releases", format names such as "PNG") stay literal in code; translators
//   never rewrite them.
// - Interpolation is `{name}` substitution only (no plurals engine, no
//   markup). Callers escape with `escapeHtml` when composing `innerHTML`.
// - Hosts bundle the shared UI directly; there is no `.js` codegen mirror.
//   `test/ui-i18n.test.mjs` fails when a renderer uses a key outside this
//   table, when a locale drops a key, or when placeholders diverge per locale.
//
// This module is erasable-syntax-only TypeScript (type aliases, plain
// functions) so node can type-strip it directly in tests.

import { de } from "./locales/de.ts";
import { fr } from "./locales/fr.ts";
import { it } from "./locales/it.ts";

export type Locale = "en" | "fr" | "de" | "it";

export const DEFAULT_LOCALE: Locale = "en";

export const SUPPORTED_LOCALES: ReadonlyArray<Locale> = ["en", "fr", "de", "it"];

let activeLocale: Locale = DEFAULT_LOCALE;

export function getLocale(): Locale {
  return activeLocale;
}

/** True only for the four shipped locale names (case-insensitive, base tag). */
export function isSupportedLocaleName(name: string): boolean {
  return normalizeLocaleName(name) !== null;
}

/**
 * Normalize one language tag to a shipped locale (`"fr-CA"` -> `"fr"`,
 * `"DE_at"` -> `"de"`). Returns null for unknown or empty tags so callers
 * fail closed to English.
 */
export function normalizeLocaleName(tag: string): Locale | null {
  const base = String(tag ?? "")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
  if (base === "en" || base === "fr" || base === "de" || base === "it") return base;
  return null;
}

/** Accept only known locales; unknown names fail closed and keep the current locale. */
export function setLocale(locale: string): boolean {
  const next = normalizeLocaleName(locale);
  if (next === null) return false;
  activeLocale = next;
  return true;
}

/**
 * Pick the best shipped locale from an `Accept-Language` header value or a
 * `navigator.languages`-style tag list. Quality values (`q=`) order header
 * entries; tags without a shipped base are skipped. Anything unparseable,
 * empty, or without a match falls back to English.
 */
export function pickLocale(input: string | ReadonlyArray<string> | null | undefined): Locale {
  if (input === null || input === undefined) return DEFAULT_LOCALE;
  const tags: Array<string> = Array.isArray(input)
    ? [...input]
    : parseAcceptLanguage(String(input));
  for (const tag of tags) {
    const match = normalizeLocaleName(tag);
    if (match !== null) return match;
  }
  return DEFAULT_LOCALE;
}

/** Order one `Accept-Language` header by descending `q`, dropping `q=0` and `*`. */
function parseAcceptLanguage(header: string): Array<string> {
  const ranked: Array<{ tag: string; q: number; order: number }> = [];
  const parts = String(header ?? "").split(",");
  for (let i = 0; i < parts.length; i++) {
    const segments = parts[i].split(";");
    const tag = segments[0].trim();
    if (tag === "" || tag === "*") continue;
    let q = 1;
    for (let s = 1; s < segments.length; s++) {
      const pair = segments[s].trim().split("=");
      if (pair.length === 2 && pair[0].trim().toLowerCase() === "q") {
        const parsed = Number(pair[1].trim());
        if (Number.isFinite(parsed)) q = parsed;
      }
    }
    if (!(q > 0)) continue;
    ranked.push({ tag, q, order: i });
  }
  ranked.sort((a, b) => (b.q !== a.q ? b.q - a.q : a.order - b.order));
  return ranked.map((entry) => entry.tag);
}

export type I18nVars = Record<string, string | number>;

const en = {
  "desktop.done.title": "Image saved",
  "desktop.done.partial": "Image saved with gaps",
  "desktop.done.size": "{width} × {height} pixels",
  "desktop.done.saved": "Saved in your chosen folder.",
  "desktop.done.open": "Open image",
  "desktop.done.reveal": "Show in folder",
  "desktop.done.openError":
    "Could not open the image. Check that a default image viewer is installed.",
  "desktop.done.folderError":
    "Could not open the containing folder. Check that a file manager is installed.",
  "desktop.done.missingError": "The saved image or folder no longer exists.",
  // Modal chrome (shared view.ts openModal).
  "view.modal.ok": "Got it",
  "view.modal.closeDialog": "Close dialog",
  "view.modal.closeTitle": "Close",
  // Desktop-app guidance modal.
  "view.desktop.title": "Dezoomify Desktop App",
  "view.desktop.subtitle":
    "High-performance native application for gigapixel museum artworks and local scans",
  "view.desktop.installer": "The unsigned {installer} for {platform} is on",
  "view.desktop.releasesLink": "GitHub Releases",
  "view.desktop.whyTitle": "Why use the Desktop App?",
  "view.desktop.why1Title": "Handles Larger Artworks:",
  "view.desktop.why1Body":
    "A browser tab can only hold a certain amount of picture. The desktop app assembles the image in memory subject to available memory and writes the finished output to disk.",
  "view.desktop.why2Title": "Saves the Finished Picture:",
  "view.desktop.why2Body":
    "Each job saves to one output file on your computer. You can queue several jobs; they save one at a time.",
  "view.desktop.why3Title": "When the Website Cannot Finish:",
  "view.desktop.why3Body":
    "The website stops the job with an error and points to the desktop app for the full-size image.",
  "view.desktop.howTitle": "How to use it",
  "view.desktop.step1":
    "Save the unsigned {installer} for {platform} from our GitHub Releases page, then install it. There is no auto-update.",
  "view.desktop.step2": "Launch Dezoomify and paste your zoomable image or manifest URL.",
  "view.desktop.step3":
    "Select your desired resolution and destination folder to save the complete composite image.",
  "view.desktop.cliTitle": "Need automation? Try the Dezoomify CLI",
  "view.desktop.cliDesc":
    "The CLI provides headless, scriptable single-job saving ideal for automated pipelines and server environments without a GUI.",
  "view.desktop.cliLink": "Save CLI from GitHub Releases",
  // Browser-extension guidance modal.
  "view.ext.title": "Dezoomify Browser Extension",
  "view.ext.subtitle":
    "Automatic viewer discovery for password-protected digital archives and complex pages",
  "view.ext.availableOn": "Available on",
  "view.ext.chromeStore": "Chrome Web Store",
  "view.ext.firefoxStore": "Firefox Browser Add-ons",
  "view.ext.whyTitle": "Why use the Browser Extension?",
  "view.ext.why1Title": "Signed-In Pages:",
  "view.ext.why1Body":
    "While you look at a zoomable image, it can find the image behind the viewer automatically, including on pages where you are signed in, such as library portals, museum subscriptions, and academic archives.",
  "view.ext.why2Title": "Easy to Use:",
  "view.ext.why2Body":
    "Press the Dezoomify button in the browser toolbar and pick the image to save, or send the job to the desktop app if the image is very large.",
  "view.ext.why3Title": "Private:",
  "view.ext.why3Body":
    "It only looks at the page you pointed it at, only after you pressed the button. It does not watch your browsing in the background.",
  "view.ext.howTitle": "How to use it in 3 steps",
  "view.ext.step1": "Install the extension from the Chrome Web Store or Firefox Browser Add-ons.",
  "view.ext.step2":
    "Navigate to the museum or library page displaying your artwork, logging in if needed.",
  "view.ext.step3":
    "Click the Dezoomify icon in your browser toolbar to automatically detect and extract the full-resolution image!",
  // Idle input section.
  "view.idle.intro": "allows you to save",
  "view.idle.zoomable": "zoomable images",
  "view.idle.zoomableTitle": "Large images in which you can navigate inside a webpage.",
  "view.idle.enterThe": "Enter the",
  "view.idle.urlAbbr": "URL",
  "view.idle.urlTitle": "Uniform Resource Locator, the address of a webpage",
  "view.idle.body":
    'of such an image in the text field below. The image will be saved at maximal resolution. You can then right-click on the image, and choose "Save As" in order to save it as a PNG file on your computer. If it doesn\'t work, read our',
  "view.idle.troubleLink": "troubleshooting guide",
  "view.idle.moreInfo": "If you want more information, read our",
  "view.idle.projectLink": "project page",
  "view.idle.license1": "This script is released under the",
  "view.idle.gplLink": "GPL",
  "view.idle.sourceLink": "See the source code",
  "view.idle.termsLink": "We decline any responsibility for an illegal use of this software",
  "view.idle.urlPlaceholder": "URL of the webpage containing your image",
  "view.idle.urlAria": "URL of the webpage containing your zoomable image",
  "view.idle.clearTitle": "Clear input",
  "view.idle.submit": "Dezoomify !",
  // Job step labels.
  "view.step.discovering": "Finding the zoomable image…",
  "view.step.choosingImage": "Image found; picking the best one…",
  "view.step.choosingLevel": "Choosing the highest resolution…",
  "view.step.preflighting": "Checking the image size…",
  "view.step.downloading": "Saving image tiles…",
  "view.step.saving": "Assembling the final picture…",
  "view.step.working": "Working…",
  "view.step.contactingDetail": "Contacting the image host…",
  "view.step.recoveryDetail": "A recovery decision needs attention before the job can continue.",
  "view.step.encodingDetail": "Encoding in the app.",
  "view.step.cleanupDetail": "Cleaning up unfinished files…",
  // Live job section.
  "view.job.workingOn": "Working on",
  "view.job.cancel": "Cancel",
  "view.job.change": "Change",
  "view.job.techDetails": "Technical details & logs",
  "view.job.oneImage": "1 image",
  "view.job.manyImages": "{count} images",
  "view.job.autoChoiceFull":
    "Found {noun}, saving largest that fits ({width}×{height}, {tiles} tiles).",
  "view.job.autoChoiceDims": "Found {noun}, saving largest that fits ({width}×{height}).",
  "view.job.autoChoiceTiles": "Found {noun}, saving largest that fits ({tiles} tiles).",
  "view.job.autoChoiceBare": "Found {noun}, saving largest that fits.",
  "view.job.stalled":
    "Still working, {host} is slow to answer. You can wait, or cancel and try again later.",
  // Display-only section.
  "view.display.title": "Showing preview – not saved yet",
  "view.display.openDesktop": "Open in desktop app",
  // Desktop handoff uses the ordinary, user-confirmed dezoomify:// link.
  "view.handoff.send": "Send to desktop app",
  "view.handoff.sendOrigin": "Send to desktop app ({origin})",
  "view.handoff.localNote":
    "Local files stay on this computer. Open the desktop app and choose the file there; nothing is sent.",
  "view.display.waysTitle": "Ways to save this artwork",
  "view.display.extTitle": "Browser Extension Guide",
  "view.display.extDesc":
    "For pages requiring login or session cookies. Automatically detects viewers on active pages.",
  "view.display.deskTitle": "Desktop App Guide",
  "view.display.deskDescClean":
    "For a clean full-size save when the browser can only show the image.",
  "view.display.startOver": "Start over",
  // Resolution downgrade notice: automatic selection took a smaller known
  // level than the maximum because of browser limits (website and extension).
  "view.resolution.notice":
    "Not downloading at maximal resolution due to browser limitations. Try the desktop app to remove browser limitations.",
  "view.resolution.sizes": "Saving at {selected} pixels instead of the maximum {maximum} pixels.",
  "view.resolution.download": "Download desktop app",
  "view.resolution.tryMaximum": "Try maximum",
  "view.resolution.stop": "Stop",
  // Completion section.
  "view.done.ready": "Your image is ready.",
  "view.done.savedDisk": "Saved to disk",
  "view.done.readyTitle": "Ready to save",
  "view.done.saveNow": "Save image now",
  "view.done.another": "Dezoomify another image",
  // Already-saved completion (ViewContext.savedOutput): the host wrote the
  // output before rendering (for example the extension blob-anchor save), so
  // completion reads as saved with the file name and offers no second-click
  // save button. Absent keeps the website ready plus save-now path.
  "view.done.savedFile": "Saved",
  "view.done.gaps": "Saved with gaps",
  "view.done.savedFull": "Saved {name} ({w}x{h}).",
  "view.done.savedPartial":
    "Saved {name} ({w}x{h}, {done} of {total} tiles; {failed} tile(s) missing).",
  // Gap map behind a kept partial: the missing-tile ledger renders inline
  // with the completion summary, so a partial save never reads as silent
  // gaps. `shown` lists the first ledger ids, `rest` names the overflow.
  "view.done.gapMap": "Missing tiles ({failed} of {total}): {shown}{rest}.",
  "view.done.gapMapMore": ", and {n} more",
  // Failure section.
  "view.fail.fallback": "Dezoomify could not find or save the zoomable image at this address.",
  "view.fail.title": "Could not dezoomify image",
  "view.fail.deskDescLimits":
    "For images that exceed browser memory limits, subject to available memory. Processes natively on your computer.",
  "view.fail.helpTitle": "Help & URL Extraction",
  "view.fail.helpDesc":
    "How to find the image address on museum & archive sites, and what to try when nothing is found.",
  "view.fail.techDetails": "Technical error details & bug report",
  "view.fail.reportBug": "Report a bug on GitHub",
  "view.fail.retry": "Try again",
  // Browser canvas failure family (allocation, 2D context, PNG encoding):
  // the desktop app is the recovery, so every message names it.
  "view.fail.canvasAllocation":
    "This picture is too large to assemble in this browser tab. The desktop app can save it at full size.",
  "view.fail.canvasContext":
    "This browser tab could not create the picture surface at this size. The desktop app can save it at full size.",
  "view.fail.canvasEncode":
    "This browser tab could not finish the full-size PNG picture. The desktop app can save it at full size.",
  "view.fail.redactHint":
    "Details below can include full addresses and the server's reply. The report link copies them into a GitHub draft; remove sign-in details and tokens before submitting.",
  // Cancelled section.
  "view.cancel.title": "Save cancelled",
  "view.cancel.message": "The image save was stopped.",
  // Generic fallback for unknown phases (debug surface; status codes stay raw).
  "view.generic.status": "Status:",
  "view.generic.reset": "Reset",
  // Job section and share chrome.
  "view.job.shareTitle": "Copies the page address for this job, not the image file itself",
  "view.job.shareLink": "Copy link to this job",
  "view.job.countsFull": "{current} of {total} tiles",
  "view.job.countsElapsed": "{current} of {total} tiles · {elapsed} elapsed",
  "view.job.elapsedOnly": "{elapsed} elapsed",
  // Recent-jobs history (todo 5.2): local-only ledger.
  "view.history.title": "Recent pictures",
  "view.history.empty": "No recent pictures yet. Saved pictures appear here.",
  "view.history.localOnly": "Kept only on this device.",
  "view.history.open": "Open again",
  "view.history.clear": "Clear history",
  "view.history.dims": "{w} by {h} pixels",
  "view.input.description":
    "Dezoomify downloads zoomable tiled images from libraries, museums, galleries, and other websites. Paste the URL of an image below to download it.",
  "view.input.placeholder": "Paste an image viewer or manifest URL",
  "view.input.aria": "Address of the webpage containing your zoomable image",
  "view.input.start": "Find image",
  // Failure "What happened" explainer.
  "view.fail.whatHappened": "What happened",
  "view.fail.rateProxy":
    "The website hosting this image limits how many pages our server may request from it, and that limit was just reached, so the page could not be opened. The browser extension and the desktop app download from your own internet connection instead of our server, so they are not affected by this limit.",
  "view.fail.rateDirect":
    "The website hosting this image is receiving too many requests from your own connection right now. Waiting a few minutes usually clears it, and the browser extension or desktop app will see the same busy signal until it does.",
  // Desktop app user copy (apps/desktop/src/main.tsx). Logs and technical
  // diagnostics stay literal English and never use these keys.
  "desktop.url.invalid": "Please enter a valid web address starting with http:// or https://",
  "desktop.url.notWebPage":
    "That address does not look like a web page address. Enter an address starting with http:// or https://.",
  "desktop.settings.unusable":
    "These download settings cannot be used. Adjust the highlighted settings and try again.",
  "desktop.settings.invalidSubmit":
    "These download settings are invalid. Adjust them and try again.",
  "desktop.output.deniedPick":
    "The save destination was not accepted. Choose a different file to continue.",
  "desktop.output.deniedFallback": "The save destination was denied.",
  "desktop.proto.incompatible":
    "This app version cannot open this picture from {host}. Update the app and try again.",
  "desktop.handoff.rejected":
    "This link cannot be opened from {host}. Try a different address without sign-in details.",
  "desktop.handoff.acceptedDetail":
    "This picture can be handed to another app. You are already in the native app, so you can continue here.",
  "desktop.handoff.rejectedDetail":
    "This picture cannot be handed to another app. Continue here or try a different picture.",
  "desktop.output.exists":
    "A file already exists at the save destination from {host}. Choose a different file or confirm overwriting to continue.",
  "desktop.output.destDenied":
    "The save destination was not accepted from {host}. Choose a different file to continue.",
  "desktop.job.gone": "This job is no longer active from {host}. Start again with a fresh address.",
  "desktop.msg.thisPicture": "this picture",
  "desktop.msg.dimsPixels": "{a} by {b} pixels",
  "desktop.msg.needAbout": " It needs about {need} of memory",
  "desktop.output.canvasLimit":
    "This picture is too large to assemble on this computer ({dims},{need} at 4 bytes per pixel, limit {limit}). Save a smaller version with Max width (CLI: --max-width). Note: JPEG saves at most {jpegMax} pixels per side; keep PNG for larger pictures. From {host}.",
  "desktop.output.jpegLimit":
    "This picture ({dims}) is too large for JPEG, which allows at most {jpegMax} pixels per side. Save it as PNG instead. From {host}.",
  "desktop.tile.partialDiscarded":
    "The partial picture was discarded so no file was kept. Try again from {host} with a steady connection.",
  "desktop.tile.partialChoice":
    "Some pieces of this picture from {host} could not be saved. Retry the failed pieces, or keep the partial picture with blank areas.",
  "desktop.discovery.none":
    "Could not find a zoomable image at this address from {host}. Try a different page or check the address.",
  "view.discovery.none":
    "No zoomable image was found at this address. Try a page that contains a zoom viewer, or try the browser extension.",
  "desktop.plan.none":
    "This picture has no usable size to save from {host}. Try a different picture or a smaller Max width.",
  "desktop.transport.stalled":
    "Saving stalled while contacting {host}. Check your connection and try again.",
  "desktop.output.writeFail":
    "Could not write this picture from {host}. Choose a different save destination and try again.",
  "desktop.job.cancelledMsg": "The image save was stopped. Any unfinished file was removed.",
  "desktop.start.failed": "Could not start saving this picture from {host}. Try again.",
  "desktop.choice.failed": "That choice was not accepted. Try again.",
  "view.ext.desynced": "The extension lost sync while reading this image. Start the scan again.",
  "desktop.save.generic":
    "Could not save this picture from {host}. Try again with a different address.",
  "desktop.internal.error":
    "Something unexpected stopped this save from {host}. Try again, and copy diagnostics if it keeps happening.",
  "desktop.save.fallback": "Could not save this picture from {host}. Try again.",
  "desktop.job.failedFallback": "The job failed.",
  "desktop.invoke.startFallback": "Could not start the job.",
  "desktop.invoke.retry": "The retry request was rejected.",
  "desktop.invoke.partial": "The partial-image choice was rejected.",
  "desktop.invoke.destination": "Could not request the save destination.",
  "desktop.invoke.cancel": "Could not cancel the job.",
  "desktop.step.chooseWhere": "Choose where to save…",
  "desktop.step.chooseWhereDetail":
    "The save destination needs attention before the job can continue.",
  "desktop.step.pickOutput": "Pick the output file to continue.",
  "desktop.step.partialTitle": "Some tiles could not be saved…",
  "desktop.step.partialDetail": "Choose whether to keep the partial image, discard it, or retry.",
  "desktop.step.displayPreview": "Display-only preview…",
  "desktop.step.displayDetail": "This picture can only be viewed here.",
  "desktop.step.cleanupDetail": "Cleaning up… removing unfinished file…",
  "desktop.step.cleaningShort": "Cleaning up…",
  "desktop.step.encodingNative": "Encoding in the native app",
  "desktop.step.encodingPartial": "Encoding partial image in the native app",
  "desktop.step.discardingPartial": "Discarding partial image",
  "desktop.step.retrying": "Retrying",
  "desktop.step.appAutoDetail":
    "The app saves the first image automatically; no picker is offered.",
  "desktop.step.foundFits": "Found {noun}, saving largest that fits…",
  "desktop.step.tilesAtFull": "{current} of {total} tiles at full resolution",
  "desktop.step.savedDims": "Saved {width} by {height} pixels",
  "desktop.step.partialDims": "Partial image {width} by {height} pixels; {summary}",
  "desktop.step.partialSaved": "Partial image saved; {summary}",
  "desktop.step.savedWord": "Saved",
  "desktop.step.contacting": "Contacting {host}…",
  "desktop.link.title": "Another app wants to open an image in Dezoomify.",
  "desktop.link.source": "Source: {url}",
  "desktop.link.prov": "Provenance: dezoomify:// link (v{version})",
  "desktop.link.provHint": "Provenance: dezoomify:// link (v{version}) · {hint}",
  "desktop.link.note": "Nothing runs until you confirm. Declining does nothing.",
  "desktop.link.dismiss": "Dismiss",
  "desktop.link.open": "Open image",
  "desktop.rec.partialTitle": "Some tiles could not be saved",
  "desktop.rec.partialDesc":
    "Part of the image is missing. {summary} Keep the partial image (blank areas stay empty), discard it, or retry the failed tiles.",
  "desktop.rec.missing": "Missing tiles: {shown}{rest}.",
  "desktop.rec.more": " and {n} more",
  "desktop.rec.destTitle": "Save destination needs attention",
  "desktop.rec.destDesc":
    "The save destination was not accepted. Choose an output file, try again, or use another app.",
  "desktop.rec.keep": "Keep partial image",
  "desktop.rec.discard": "Discard partial",
  "desktop.rec.retryTiles": "Retry failed tiles",
  "desktop.rec.chooseOutput": "Choose output…",
  "desktop.rec.tryAgain": "Try again",
  "desktop.rec.useOther": "Use another app",
  "desktop.rec.missingSome": "Some tiles could not be saved.",
  "desktop.rec.missingCount": "{count} tile{plural} could not be saved.",
  "desktop.rec.missingList": "{n} tile{plural} missing: {shown}{rest}.",
  "desktop.done.partialTitle": "Partial image saved",
  "desktop.done.partialDesc":
    "This file is marked as partial: {summary} Missing areas are left blank. This distinguishes it from a complete save.",
  "desktop.cancel.note": "Save cancelled. Cleanup is done and any unfinished file was removed.",
  "desktop.copy.diagnostics": "Copy diagnostics",
  "desktop.copy.copied": "Copied!",
  // Multi-job queue panel (desktop integration queue, todo 5.3). Jobs save
  // one at a time in the order they were added; a failed job never stops the
  // rest. Only redacted origins appear here, never full addresses.
  "desktop.queue.title": "Queue",
  "desktop.queue.statusQueued": "Waiting",
  "desktop.queue.statusActive": "Running",
  "desktop.queue.statusDone": "Done",
  "desktop.queue.statusFailed": "Failed",
  "desktop.queue.statusCancelled": "Cancelled",
  "desktop.queue.cancel": "Cancel",
  "desktop.queue.cancelAll": "Cancel all",
  "desktop.queue.retry": "Retry",
  "desktop.queue.summary": "{succeeded} done, {failed} failed, {total} total",
  "desktop.queue.progress": "{current} of {total} tiles",
  "desktop.queue.unknownOrigin": "the server",
  "desktop.panel.outputFormat": "Output format",
  "desktop.panel.jobActions": "Desktop job actions",
  "desktop.help.title": "Help and about",
  "desktop.help.help": "Help",
  "desktop.help.desktopGuide": "Desktop guide",
  "desktop.help.troubleshooting": "Troubleshooting",
  "desktop.help.faq": "FAQ",
  "desktop.help.privacy": "Privacy",
  "desktop.help.terms": "Terms",
  "desktop.help.donate": "Donate",
  "desktop.settings.title": "Customize",
  "desktop.settings.desc":
    "Minimal download settings. Saved on this device and used for the next job. Headers are sent to the image origin only and never logged.",
  "desktop.settings.fileGroup": "File",
  "desktop.settings.imageGroup": "Image",
  "desktop.settings.networkGroup": "Network and recovery",
  "desktop.settings.outputDir": "Output directory (optional)",
  "desktop.settings.compression": "Compression 0-100 (default 5)",
  "desktop.settings.maxWidth": "Max width px (optional)",
  "desktop.settings.maxHeight": "Max height px (optional)",
  "desktop.settings.retries": "Retries 0-100 (default 3, 0 = none)",
  "desktop.settings.cacheDir": "Cache directory (optional resume cache)",
  "desktop.settings.emptyLargest": "empty = largest",
  "desktop.settings.browse": "Browse…",
  "desktop.settings.browseOutput": "Browse for output directory",
  "desktop.settings.browseCache": "Browse for cache directory",
  "desktop.settings.headersAdv": "Advanced: request headers (trusted)",
  "desktop.settings.headersLabel":
    "Request headers, one per line as Name: value (optional, trusted)",
  "desktop.settings.reset": "Reset settings",
  "desktop.quick.folder": "Folder",
  "desktop.quick.askEachTime": "Ask each time",
  "desktop.quick.chosenFolder": "Chosen folder",
  "desktop.quick.chooseFolder": "Choose a starting folder for the save dialog",
  "desktop.quick.format": "Format",
  "desktop.quick.size": "Size",
  "desktop.quick.network": "Network",
  "desktop.quick.fast": "Fast",
  "desktop.quick.balanced": "Balanced · 5/s",
  "desktop.quick.gentle": "Gentle · 2/s",
  "desktop.quick.fullResolution": "Full resolution",
  "desktop.quick.upTo4k": "Up to 4K",
  "desktop.quick.upTo2k": "Up to 2K",
  "desktop.quick.custom": "Custom…",
  "desktop.quick.more": "More settings",
  "desktop.advanced.title": "Advanced settings",
  "desktop.advanced.done": "Done",
  "desktop.advanced.jpegQuality": "JPEG quality",
  "desktop.advanced.jpegQualityDesc": "Higher keeps more image detail.",
  "desktop.advanced.compressionEffort": "Compression effort",
  "desktop.advanced.compressionEffortDesc":
    "Image quality stays lossless; higher values take longer.",
  "desktop.advanced.dimensions": "Custom dimensions",
  "desktop.advanced.dimensionsDesc":
    "Leave either value empty to preserve the original proportion.",
  "desktop.advanced.width": "Width",
  "desktop.advanced.height": "Height",
  "desktop.advanced.retries": "Retries",
  "desktop.advanced.retriesDesc": "Try failed image tiles again before keeping a partial result.",
  "desktop.advanced.resumeCache": "Resume cache",
  "desktop.advanced.resumeCacheDesc": "Reuse tiles after an interrupted save.",
  "desktop.advanced.choose": "Choose…",
  "desktop.advanced.change": "Change…",
  "desktop.advanced.headers": "Request headers",
  "desktop.advanced.headersDesc":
    "For protected viewers. Sent only to the image origin and never logged.",
  // Extension job-tab user copy, rendered through the same `t(key, vars)`
  // shape; log and diagnostics lines stay literal English and never use these
  // keys. `test/ui-i18n.test.mjs` fails when the page renders a key outside
  // this table.
  "page.step.scanning": "Scanning page…",
  "page.step.finding": "Finding the zoomable image ({done}/{total})…",
  "page.step.choosing": "Choosing the highest resolution…",
  "page.step.saving": "Saving image tiles…",
  "page.step.assembling": "Assembling the final picture…",
  "page.step.done": "Done",
  "page.step.cancelled": "Cancelled",
  "page.step.cancelling": "Cancelling…",
  "page.step.displaying": "Displaying the image…",
  "page.tabs.scan": "Scan {label}",
  "page.tabs.hint":
    "Open a page with a zoomable image, then click the Dezoomify toolbar button to scan that tab.",
  "page.ui.techDetails": "Technical details & logs",
} as const;

export type I18nKey = keyof typeof en;

/** Canonical English templates. Tests enumerate this table. */
export const EN: Record<string, string> = en;

/** French templates (same keys, same placeholders). */
export const FR: Record<string, string> = fr;

/** German templates (same keys, same placeholders). */
export const DE: Record<string, string> = de;

/** Italian templates (same keys, same placeholders). */
export const IT: Record<string, string> = it;

const dictionaries: Record<string, Record<string, string>> = { en, fr, de, it };

/** Read one locale table with English fallback (never undefined). */
export function getDictionary(locale: string): Record<string, string> {
  const match = normalizeLocaleName(locale);
  if (match !== null) return dictionaries[match] ?? EN;
  return EN;
}

/**
 * Render one message with `{var}` substitution in the active locale (or an
 * explicit locale override). Unknown keys fall back to the key itself; keys
 * missing from the active locale fall back to English per key.
 */
export function t(key: I18nKey, vars?: I18nVars, locale?: string): string {
  const want: string =
    typeof locale === "string" && locale !== ""
      ? (normalizeLocaleName(locale) ?? activeLocale)
      : activeLocale;
  const table: Record<string, string> = dictionaries[want] ?? EN;
  const template: string = table[key as string] ?? EN[key as string] ?? (key as string);
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
}
